import { filterClasses } from "@swivl-mono/helpers";
import {
  FacilityAndSpaceFeatureDef,
  FeatureCategory,
  FeatureSwitchDef,
  LocationVarInheritance,
  LocationVarKey,
  MergedLocationVar,
  featureOnValue,
  findURLDef,
  isFeatureOn,
  isFeatureSwitch
} from "@swivl-mono/types";
import { Input, Tooltip, VariableTypeahead } from "@swivl-mono/ui";
import { useState } from "react";
import { GrRevert } from "react-icons/gr";
import { TbChevronRight } from "react-icons/tb";

import { usePropertiesGetLocationIds } from "../../../../Hooks/usePropertiesGetLocationIds";
import { WidgetSwitch } from "../../../Widgets/WidgetSwitch";
import { PolicyHint } from "../../PolicyWorksheets/PolicyHint";
import { TopicDraftEntry, useTopicContext, useTopicHandlers, useTopicRow } from "../Hooks/useTopicContext";
import { CortexInheritancePill } from "./CortexInheritancePill";
import { revertLabelFor } from "./revertTarget";

interface CortexFeatureRowProps {
  feature: FacilityAndSpaceFeatureDef;
  /** Org is the top scope — a revert there lands on the VarDef default. */
  isOrgScope: boolean;
  /** How the next scope up resolves the feature's on/off (switches) or name (unit tags) — names the revert's destination. */
  parentInheritance: LocationVarInheritance | undefined;
  /** Display text for the state a revert would restore. */
  revertsTo: string;
}

/** Per-category word used in the placeholder copy. */
const CATEGORY_WORD: Record<FeatureCategory, string> = {
  amenity: "amenity",
  service: "service",
  storage_type: "storage",
  unit_attribute: "unit feature"
};

/** One bound location variable of a feature: its merged row, staged draft, and handlers. */
interface BoundVar {
  key: LocationVarKey;
  merged: MergedLocationVar;
  draft: TopicDraftEntry | undefined;
  value: string;
  isOverride: boolean;
  overrideStaged: boolean;
  /** A revert of this var's saved override is staged until Save (ENG-3057). */
  revertStaged: boolean;
  set: (next: string) => void;
  revert: () => void | Promise<void>;
  discard: () => void;
}

function useBoundVar(key: LocationVarKey): BoundVar {
  const { merged, draft } = useTopicRow(key);
  const handlers = useTopicHandlers(key);
  return {
    key,
    merged,
    draft,
    value: draft?.value ?? merged.value,
    isOverride: merged.inheritance === LocationVarInheritance.NONE,
    overrideStaged: draft?.value !== undefined,
    revertStaged: draft?.revert === true,
    set: handlers.onSettingValue,
    revert: () => handlers.onRevert(merged),
    discard: handlers.onDiscard
  };
}

/**
 * One feature = up to three ordinary location variables (on/off, display name,
 * wording), each inheriting on its own. This row presents them as ONE thing: one
 * inheritance pill ("custom" if any of the three is overridden at this scope), one
 * Revert (reverts every overridden one). Name on the left, availability toggle in
 * the middle, pill + Revert on the right; expand for the name and wording fields.
 * A blank name or wording means the catalog default — no separate "use this" flag.
 *
 * A `unit`-scope feature (a tag FMS sync writes onto units — first floor, roll door,
 * …) has no availability to toggle and no wording, so its row is name-only (ENG-3209).
 */
export function CortexFeatureRow(props: CortexFeatureRowProps): React.ReactElement {
  return isFeatureSwitch(props.feature) ? <SwitchRow {...props} feature={props.feature} /> : <UnitTagRow {...props} />;
}

// ─── Shared shell ──────────────────────────────────────────────────────────

interface FeatureShellProps {
  feature: FacilityAndSpaceFeatureDef;
  vars: BoundVar[];
  /** The var whose inheritance state the pill shows when nothing is staged (on/off for switches, name for tags). */
  primary: BoundVar;
  displayName: string;
  isOrgScope: boolean;
  parentInheritance: LocationVarInheritance | undefined;
  revertsTo: string;
  /** What sits where a switch would; unit tags have none, so they leave it out. */
  control?: React.ReactNode;
  fields: React.ReactNode;
  /** Switches only — gates the paired-URL hint, which only makes sense once the feature is on. */
  isOn?: boolean;
}

function FeatureShell({
  feature,
  vars,
  primary,
  displayName,
  isOrgScope,
  parentInheritance,
  revertsTo,
  control,
  fields,
  isOn
}: FeatureShellProps): React.ReactElement {
  const { isEditing } = useTopicContext();
  const [expanded, setExpanded] = useState<boolean>(false);

  // Paired-URL hint state. Unconditional hook call; fallback key unused.
  const urlDef = feature.pairedUrlKey ? findURLDef(feature.pairedUrlKey) : undefined;
  const urlRow = useTopicRow(urlDef?.urlVarKey ?? primary.key);
  const linkedUrl = (urlRow.draft?.value ?? urlRow.merged.value ?? "").trim();
  const page = usePropertiesGetLocationIds();

  const isOverride = vars.some((v) => v.isOverride);
  const overrideStaged = vars.some((v) => v.overrideStaged);
  const customized = isOverride || overrideStaged;
  // A staged revert (ENG-3057): the controls already show the destination value, so the
  // pill shows the destination tier and the action undoes the revert instead of repeating it.
  const revertStaged = vars.some((v) => v.revertStaged);
  const revertInheritance = vars.find((v) => v.revertStaged)?.draft?.revertInheritance ?? LocationVarInheritance.DEFAULT;
  const revertLabel = revertLabelFor({ isOrgScope, isOverride, inheritance: primary.merged.inheritance, parentInheritance });

  const handleRevert = (): void => {
    if (!window.confirm(`${revertLabel} for ${feature.label}? This discards the customization at this level.`)) return;
    // A saved override is deleted server-side, which also clears that var's draft.
    // With nothing saved yet, dropping the draft is the whole job.
    for (const v of vars) {
      if (v.isOverride) void v.revert();
      else if (v.overrideStaged) v.discard();
    }
  };

  return (
    <div className="mx-4 border-b border-grey-200 py-3 last:border-b-0">
      {/* Wider label column than Facility Info: a custom display name is
          operator-typed, so it can run much longer than a fixed field label. */}
      <div className="grid grid-cols-[280px_1fr] items-center gap-x-4">
        {/* Expandable in both modes — read-only just shows the fields disabled. */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="body-sm-alt flex min-w-0 cursor-pointer items-center gap-1 text-left text-grey-900"
          title={feature.description}
        >
          <TbChevronRight className={filterClasses("size-4 shrink-0 text-grey-400 transition-transform", expanded && "rotate-90")} />
          <span className="break-words">{displayName}</span>
        </button>

        <div className="flex min-w-0 items-center gap-2">
          {/* Both wrappers are flex, not block: these children are inline-level,
              so a block wrapper would give them a text line box taller than the
              control and knock them off the row's centre line. */}
          <div className="flex min-w-0 flex-1 items-center">{control}</div>

          <span className="flex shrink-0 items-center">
            <CortexInheritancePill
              inheritance={
                revertStaged
                  ? revertInheritance
                  : overrideStaged
                    ? LocationVarInheritance.NONE
                    : isOverride
                      ? LocationVarInheritance.NONE
                      : primary.merged.inheritance
              }
            />
          </span>

          {isEditing && revertStaged && (
            <button
              type="button"
              onClick={() => vars.forEach((v) => v.revertStaged && v.discard())}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              Undo revert
              <GrRevert className="size-4" />
            </button>
          )}

          {isEditing && !revertStaged && customized && (
            <Tooltip content={`Reverts to: ${revertsTo}`}>
              <button
                type="button"
                onClick={handleRevert}
                className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
              >
                {revertLabel}
                <GrRevert className="size-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Always visible when on, not behind the arrow. */}
      {urlDef && isOn && (
        <div className="pl-[296px]">
          <PolicyHint
            body={linkedUrl ? `Linked to ${linkedUrl} -` : `Have a ${urlDef.label.toLowerCase()} page?`}
            linkLabel={linkedUrl ? "Edit in URLs" : "Add it in URLs"}
            linkHref={`${page.preamble}/page/details-v3/urls`}
          />
        </div>
      )}

      {/* Name (+ wording for switches). Visible in both modes; outside edit mode
          the controls are disabled. Indented to line up with the control column
          (280px label + 16px gap). */}
      {expanded && <div className="mt-3 space-y-3 pl-[296px]">{fields}</div>}
    </div>
  );
}

function NameField({ feature, name, isEditing }: { feature: FacilityAndSpaceFeatureDef; name: BoundVar; isEditing: boolean }): React.ReactElement {
  return (
    <div>
      <p className="body-sm text-grey-900">Custom display name</p>
      <Input
        containerClassName="mt-1"
        disabled={!isEditing}
        value={name.value}
        placeholder={feature.label}
        onChange={(e) => name.set(e.target.value)}
      />
      <p className="body-sm mt-1 text-grey-600">Leave empty to use the default name.</p>
    </div>
  );
}

// ─── Operator switch: on/off + name + wording ──────────────────────────────

function SwitchRow({ feature, isOrgScope, parentInheritance, revertsTo }: CortexFeatureRowProps & { feature: FeatureSwitchDef }): React.ReactElement {
  const { isEditing } = useTopicContext();
  const on = useBoundVar(feature.cortexVarKey);
  const name = useBoundVar(feature.nameVarKey);
  const text = useBoundVar(feature.botWordingVarKey ?? feature.nameVarKey);
  const hasText = feature.botWordingVarKey !== undefined;

  const isOn = isFeatureOn(on.value);
  const displayName = name.value.trim() || feature.label;
  const policyWord = CATEGORY_WORD[feature.category];

  return (
    <FeatureShell
      feature={feature}
      vars={hasText ? [on, name, text] : [on, name]}
      primary={on}
      displayName={displayName}
      isOrgScope={isOrgScope}
      parentInheritance={parentInheritance}
      revertsTo={revertsTo}
      isOn={isOn}
      control={
        // WidgetSwitch carries its own disabled styling, so a read-only or inherited row dims without an extra wrapper.
        <WidgetSwitch on={isOn} disabled={!isEditing} onSet={(next) => on.set(featureOnValue(next))} ariaLabel={feature.label} />
      }
      fields={
        <>
          <NameField feature={feature} name={name} isEditing={isEditing} />
          {hasText && (
            <div>
              <p className="body-sm text-grey-900">Custom wording</p>
              <VariableTypeahead
                containerClassName="mt-1"
                name={text.key}
                disabled={!isEditing}
                value={text.value}
                placeholder="Leave empty to use the standard wording."
                onChange={(next) => text.set(next)}
              />
              {/* Not the field's `helperText`: that renders through the shared
                  HelperText at a fixed mt-0.5, and widening it would shift helper
                  text on every form field in studio. Typing `{{` in the field
                  opens the variable picker (ENG-3209). */}
              <p className="body-sm mt-1 text-grey-600">
                Reference text the bot uses to describe this {policyWord} — it paraphrases this, it doesn't read it word-for-word.
              </p>
            </div>
          )}
        </>
      }
    />
  );
}

// ─── Unit tag: name only ───────────────────────────────────────────────────

function UnitTagRow({ feature, isOrgScope, parentInheritance, revertsTo }: CortexFeatureRowProps): React.ReactElement {
  const { isEditing } = useTopicContext();
  const name = useBoundVar(feature.nameVarKey);
  const displayName = name.value.trim() || feature.label;

  return (
    <FeatureShell
      feature={feature}
      vars={[name]}
      primary={name}
      displayName={displayName}
      isOrgScope={isOrgScope}
      parentInheritance={parentInheritance}
      revertsTo={revertsTo}
      // A unit tag's availability comes from the FMS per unit — nothing to switch, nothing to say.
      fields={<NameField feature={feature} name={name} isEditing={isEditing} />}
    />
  );
}
